/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import includes from 'lodash/includes';
import isNil from 'lodash/isNil';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import { isSameDay, toZoned, type DateValue } from '@internationalized/date';
import { calendarDateToUtcString, localNow, localToday } from 'sarsaparilla';

import {
    ITINERARY_TYPE_FACILITY,
    ADDRESS_TYPE_VALUES,
    MAILING_ADDRESS,
    PHYSICAL_ADDRESS,
} from '../constants/campground';
import {
    getDaysDifference,
    isBetweenDates,
    isDateAfter,
    UTCStringToCalendarDate,
} from './dateUtils';
import type { FacilityType } from '../../../types/enums';
import type {
    Address,
    Campground,
    CampsiteRules,
    FacilityRules,
    Season,
} from '../../../types/camping';
import { DEFAULT_TIMEZONE } from '../constants';

export const countSiteTypes = (siteTypes: any) => {
    let single = 0;
    let group = 0;

    forEach(siteTypes, (type) => {
        if (includes(type.name, 'GROUP')) {
            group += type.count;
        } else {
            single += type.count;
        }
    });

    return { single, group };
};

export const getWalkupDisplayText = (facilityType: FacilityType) => {
    if (includes(facilityType, ITINERARY_TYPE_FACILITY)) {
        return 'Walk-up only';
    }

    return 'First-come, First-served';
};

export const getWalkupAbbrDisplayText = (facilityType: FacilityType) => {
    if (includes(facilityType, ITINERARY_TYPE_FACILITY)) {
        return 'W';
    }

    return 'FF';
};

export const getReservationWindowValue = (
    facilityRules: FacilityRules,
    isGroupType: boolean = false
) => {
    let value = facilityRules?.reservationWindow?.value ?? 6;
    let unit = includes(
        ['Days', 'Day'],
        facilityRules?.reservationWindow?.units ?? 'months'
    )
        ? 'days'
        : 'months';

    if (isGroupType) {
        value = facilityRules?.groupReservationWindow?.value ?? 12;
        unit = includes(
            ['Days', 'Day'],
            facilityRules?.groupReservationWindow?.units ?? 'months'
        )
            ? 'days'
            : 'months';
    }

    return {
        value,
        unit,
    };
};

/**
 * calculates the no modification period based on facility's no mod rule and reservation dates
 */
export const calculateNoModRule = ({
    campground,
    reservationEndDate,
    campsiteType,
    campsiteRules,
}: {
    campground: Campground;
    reservationEndDate: string;
    campsiteType: string;
    campsiteRules: CampsiteRules;
}) => {
    const NO_MOD_RULE_DAYS = 4;
    const reservationEnd = UTCStringToCalendarDate(reservationEndDate);
    let bookingWindow = localToday;
    const facilityRules = campground?.facility_rules ?? {};
    const isGroupSite = !isNil(campsiteType) && campsiteType.includes('GROUP');
    let value;
    let unit;

    if (!isEmpty(campsiteRules)) {
        ({ unit, value } = getReservationWindowValue(campsiteRules, isGroupSite));
    } else {
        ({ unit, value } = getReservationWindowValue(facilityRules, isGroupSite));
    }

    // subtracting 1 because it refers to number of nights not days
    bookingWindow = bookingWindow.subtract({ days: NO_MOD_RULE_DAYS - 1 });
    bookingWindow = bookingWindow.add({ [unit]: value });

    /* reservation CANNOT be modified (need to set daysToEdit and editableDate values) if...
        1. reservation end date is AFTER the release window end
        2. reservation end date is SAME as the release window end, but it's not 10AM EST yet (after 10AM release time, res can be modified)
    */
    const doesResEndAfterReleaseWindow =
        isDateAfter(reservationEnd, bookingWindow) ||
        (isSameDay(reservationEnd, bookingWindow) &&
            toZoned(localNow, DEFAULT_TIMEZONE).hour < 10);
    if (doesResEndAfterReleaseWindow) {
        return getDaysDifference(
            reservationEndDate,
            calendarDateToUtcString(bookingWindow)!
        );
    }
    return null;
};

export const isMailingAddress = (address: Address) => {
    return includes(ADDRESS_TYPE_VALUES[MAILING_ADDRESS], address.address_type);
};

export const isPhysicalAddress = (address: Address) => {
    return includes(ADDRESS_TYPE_VALUES[PHYSICAL_ADDRESS], address.address_type);
};

export const getDateBlockedWithinSeason = (
    seasonData: Season[],
    day: DateValue | string
) => {
    for (const x in seasonData) {
        if (isBetweenDates(day, seasonData[x].season_start, seasonData[x].season_end)) {
            return !!(
                seasonData[x].season_description === 'First-come, First-served Season'
            );
        }
    }
    return true;
};

export const isItineraryFacility = (facilityType: FacilityType) =>
    includes(facilityType, ITINERARY_TYPE_FACILITY);
